import { ReactNode } from "react";
import { Modal, type ModalProps } from "@ui/modal";
import { Button } from "@ui/button";
import { cn } from "@/utils/cn";
import CodeTextarea from "@ui/highlight-code-textarea";

type BaseCodeEditorModalProps = Omit<ModalProps, "children"> & {
  title: string;
  code: string;
  onCodeChange: (value: string) => void;
  minLines: number;
  placeholder: string;
  codeError?: string;
  topContent?: ReactNode;
  extraContent?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  maxWidthClassName?: string;
  presentation?: "default" | "summary";
};

export function BaseCodeEditorModal({
  title,
  code,
  onCodeChange,
  minLines,
  placeholder,
  codeError,
  topContent,
  extraContent,
  onCancel,
  onConfirm,
  maxWidthClassName = "max-w-[800px]",
  presentation = "default",
  ...modalProps
}: BaseCodeEditorModalProps) {
  const isSummaryPresentation = presentation === "summary";

  return (
    <Modal
      {...modalProps}
      size="xl"
      className={cn(
        isSummaryPresentation && "shadow-[0_4px_12px_rgba(13,10,44,0.06)]",
        maxWidthClassName,
        modalProps.className,
      )}
      closeOnOverlayClick={false}
    >
      <div
        className={cn(
          "flex flex-col items-center overflow-y-auto",
          isSummaryPresentation ? "gap-[30px] p-[30px]" : "gap-7 p-7",
        )}
      >
        <div
          className={cn(
            "w-full text-center font-semibold text-dark",
            isSummaryPresentation
              ? "text-2xl leading-[30px]"
              : "text-2xl leading-8",
          )}
        >
          {title}
        </div>

        {topContent}

        <div
          className={cn(
            "w-full flex flex-col",
            isSummaryPresentation ? "gap-5" : "gap-4",
          )}
        >
          <CodeTextarea
            value={code}
            onChange={onCodeChange}
            minLines={minLines}
            className={cn(
              "w-full",
              isSummaryPresentation &&
                "[&_.cm-editor]:!rounded-lg [&_.cm-editor]:!border-0 [&_.cm-editor]:!bg-gray-2 [&_.cm-gutters]:!border-0",
            )}
            placeholder={placeholder}
          />

          {codeError && (
            <span className="text-red text-sm font-medium">{codeError}</span>
          )}

          {extraContent}

          <button
            type="button"
            onClick={() => {
              open("/doc");
            }}
            className={cn("text-giant-blue text-base font-medium text-left")}
          >
            {isSummaryPresentation
              ? "View Guidelines"
              : "View Expression Guidelines"}
          </button>
        </div>

        <div
          className={cn(
            "flex w-full justify-center",
            isSummaryPresentation ? "gap-[18px]" : "gap-4",
          )}
        >
          <Button
            type="button"
            variant="tertiary"
            className={cn(
              "w-48",
              isSummaryPresentation && "h-[50px] w-[190px] text-base",
            )}
            onClick={onCancel}
          >
            {isSummaryPresentation ? "Cancel" : "Close"}
          </Button>
          <Button
            type="button"
            className={cn(
              "w-48",
              isSummaryPresentation && "h-[50px] w-[190px] text-base",
            )}
            onClick={onConfirm}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
